import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useProfileQuery, type Profile } from '@entities/Profile';
import { paths } from '@shared/config';
import { campaignSearch } from '@shared/lib/campaignParams';
import { rememberReturnTo } from '@shared/lib/returnTo';
import type { AuthStage, Lang, Pace, User } from '@shared/types';
import { useAuthStore } from '../model/useAuthStore';
import { useInterfaceLanguageSync } from '../model/useInterfaceLanguageSync';
import { FullScreenLoader } from './FullScreenLoader';

interface AppGateProps {
  children: ReactNode;
}

const stagePath: Record<AuthStage, string> = {
  auth: paths.auth,
  onboarding: paths.onboarding,
  app: paths.dashboard,
};

const initialsFrom = (source: string): string =>
  source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('') || '·';

const profileToUserPatch = (p: Profile): Partial<User> => {
  const paceMap: Record<Profile['pace_learning'], Pace> = {
    Low: 'relaxed',
    Medium: 'regular',
    High: 'intense',
  };
  const langMap: Record<Profile['language_interface'], Lang> = {
    Russian: 'RU',
    English: 'EN',
  };
  return {
    name: p.user.name,
    email: p.user.email,
    emailVerified: p.user.is_email_verified,
    guestCardAvailable: p.guest_card_available === true,
    initials: initialsFrom(p.user.name || p.user.email),
    level: p.english_level,
    dailyGoal: p.target_cards_per_day,
    newPerSession: p.new_cards_per_session,
    autoAdvance: p.auto_advance,
    notifications: p.is_notification,
    sounds: p.is_sound_active,
    pace: paceMap[p.pace_learning],
    lang: langMap[p.language_interface],
    role: p.role,
    hasDonated: p.user.has_donated,
  };
};

export function AppGate({ children }: AppGateProps) {
  const location = useLocation();
  const stage = useAuthStore((s) => s.stage);
  const setStage = useAuthStore((s) => s.setStage);
  const patchUser = useAuthStore((s) => s.patchUser);
  const profileQuery = useProfileQuery({ enabled: stage === 'app' });
  const syncLanguage = useInterfaceLanguageSync();
  const needsOnboarding = profileQuery.data ? !profileQuery.data.completed_onboarding : false;

  useEffect(() => {
    if (needsOnboarding) {
      // Вошёл, но знакомство не пройдено: после него OnboardingGate вернёт сюда (returnTo, Ф0).
      rememberReturnTo(location.pathname + location.search);
      setStage('onboarding');
    }
  }, [needsOnboarding, setStage, location.pathname, location.search]);

  // Гость открыл страницу приложения и уходит на вход — запоминаем, куда он шёл: после входа
  // RequireStage и OnboardingGate вернут его сюда (returnTo, Ф0). Стадия читается из стора, а не
  // из зависимостей: выход из аккаунта на этой странице (стадия сменилась, адрес тот же) ничего не
  // запоминает — после нового входа откроется главная.
  useEffect(() => {
    if (useAuthStore.getState().stage !== 'app') rememberReturnTo(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (profileQuery.data) {
      // Язык интерфейса — из профиля (важно на новом устройстве, где lx.lang ещё не выставлен),
      // но выбранный при регистрации главнее: профиль с русским по умолчанию его догоняет (B4).
      const shown = syncLanguage(profileQuery.data);
      patchUser({ ...profileToUserPatch(profileQuery.data), lang: shown === 'en' ? 'EN' : 'RU' });
    }
  }, [profileQuery.data, patchUser, syncLanguage]);

  if (stage !== 'app') {
    // Метки кампании едут с гостем на /auth — их увидят GA4 и Amplitude (см. campaignSearch).
    return <Navigate to={{ pathname: stagePath[stage], search: campaignSearch(location.search) }} replace />;
  }

  if (profileQuery.isPending || needsOnboarding) {
    return <FullScreenLoader />;
  }

  return <>{children}</>;
}
